import React from 'react'

import { datadogRum } from '@datadog/browser-rum'
import type { Map } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import { usePrevious } from 'react-use'

import type { RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useInStockRecipeIds } from 'hooks/menu-recipes/useInStockRecipeIds'
import {
  getBasketMenuId,
  getBasketMenuUuid,
  getBasketRecipes,
  getBasketSlotId,
} from 'selectors/basket'
import { getUTMAndPromoCode } from 'selectors/tracking'

import { useRecipeLimitReached } from '../limits'

const datadogActionByView: Record<string, string> = {
  view_recipe_list_screen: 'add_recipe_to_basket_via_recipe_grid',
  view_recipe_detail_screen: 'add_recipe_to_basket_via_recipe_details',
  menu_search_screen: 'add_recipe_to_basket_via_search',
}

export type AddRecipeInfo = {
  position: string
  source?: 'merch_banner'
  merch_header_type?: 'campaign-banner' | 'compact-campaign-banner'
}

export const useAddValidRecipeToBasket = () => {
  const menuRecipes = useSelector(getBasketRecipes)
  const slotId = useSelector(getBasketSlotId)
  const { promoCode, UTM } = useSelector(getUTMAndPromoCode)
  const currentMenuId = useSelector(getBasketMenuId)
  const currentMenuUUID = useSelector(getBasketMenuUuid)

  const currentCollectionId = useCurrentCollectionId()
  const currentCollection = useCollection(currentCollectionId)
  const prevRecipes = usePrevious(menuRecipes)
  const inStockRecipes = useInStockRecipeIds()

  const reachedLimit = useRecipeLimitReached(menuRecipes as unknown as Map<RecipeCoreId, number>)

  const dispatch = useDispatch()

  return React.useCallback(
    (
      recipeId: RecipeUUID,
      view?: string,
      recipeInfo?: AddRecipeInfo,
      orderId?: string,
      isOrderRecipe?: boolean, // if this is true, the recipe was already part of the order and we are just initialising the basket
    ) => {
      const outOfStock = !inStockRecipes.has(recipeId)

      const indexOfCollection = currentCollection?.index ?? -1

      if ((reachedLimit || outOfStock) && !isOrderRecipe) {
        return
      }

      if (recipeInfo) {
        Object.assign(recipeInfo, { collection: currentCollectionId })
      }

      dispatch({
        type: actionTypes.BASKET_RECIPE_ADD,
        recipeId,
        ...recipeInfo,
        isOrderRecipe,
        orderId,
        trackingData: view
          ? {
              actionType: trackingKeys.addRecipe,
              recipe_uuid: recipeId,
              menu_id: currentMenuId,
              menu_uuid: currentMenuUUID,
              event_screen: view,
              collection_id: currentCollectionId,
              ...(currentCollection?.slug && { collection_slug: currentCollection.slug }),
              collection_position: indexOfCollection + 1,
              recipe_position: recipeInfo && recipeInfo.position,
              ...(recipeInfo?.source && { source: recipeInfo.source }),
              ...(recipeInfo?.merch_header_type && {
                merch_header_type: recipeInfo.merch_header_type,
              }),
              basket_recipe_count: menuRecipes.size + 1, // The action is performed in the same time so the size is not updated yet
            }
          : undefined,
      })

      if (reachedLimit) {
        dispatch({
          type: actionTypes.BASKET_LIMIT_REACHED,
          limitReached: reachedLimit,
          trackingData: {
            actionType: trackingKeys.basketLimit,
            limitReached: reachedLimit,
            view,
            source: actionTypes.RECIPE_ADDED,
          },
        })
      }

      let recipesCount = 0

      menuRecipes.forEach((count = 0) => {
        recipesCount += count
      })

      const datadogAction = view ? datadogActionByView[view] : undefined
      if (datadogAction) {
        datadogRum.addAction(datadogAction, {
          recipesCount,
          menuId: currentMenuUUID,
          recipeId: recipeId,
        })
      }

      if (prevRecipes && prevRecipes.size < 2 && recipesCount > 1 && slotId) {
        dispatch({
          type: actionTypes.BASKET_ELIGIBLE_TRACK,
          trackingData: {
            actionType: trackingKeys.basketEligible,
            ...UTM,
            promoCode,
            menuRecipes,
          },
        })
      }
    },
    [
      inStockRecipes,
      currentCollection?.index,
      reachedLimit,
      dispatch,
      currentMenuId,
      currentMenuUUID,
      currentCollectionId,
      currentCollection?.slug,
      menuRecipes,
      prevRecipes,
      slotId,
      UTM,
      promoCode,
    ],
  )
}
